import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchInput } from '@/components/shared/SearchInput';
import { Badge } from '@/components/ui/badge';
import { useGetUsers, useAssignUserToCompany } from '@/services/api/endpoints/UserService';
import { useDebounce } from '@/hooks/useDebounce';

interface AssignUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
}

export function AssignUserModal({ open, onOpenChange, companyId }: AssignUserModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const { data: usersData, isLoading } = useGetUsers(
    1,
    10,
    debouncedSearch,
    'createdAt',
    'desc'
  );

  const assignMutation = useAssignUserToCompany();

  const filteredUsers = usersData?.items.filter(
    (user) => user.companyId !== companyId
  ) || [];

  const handleAssign = async (userId: string) => {
    try {
      await assignMutation.mutateAsync({ userId, companyId });
      toast.success(t('companies.assignSuccess'));
      onOpenChange(false);
      setSearch('');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setSearch('');
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('companies.assignUser')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('companies.searchUsers')}
          />

          <div className="max-h-[320px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {t('companies.noUsersFound')}
              </div>
            ) : (
              <div className="divide-y">
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    className="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-muted/50 transition-colors rounded-md disabled:opacity-50"
                    onClick={() => handleAssign(user.id)}
                    disabled={assignMutation.isPending}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{user.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {user.firstName || ''} {user.lastName || ''}
                        {user.company ? ` - ${user.company.name}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {t(`users.roles.${user.role}`)}
                      </Badge>
                      <UserPlus className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
